(() => {
  "use strict";

  const theme = window.TelemmTheme || {};
  const body = document.body;
  const header = document.querySelector("[data-site-header]");
  const menuToggle = document.querySelector("[data-menu-toggle]");
  const siteNav = document.querySelector("[data-site-nav]");
  const cartDrawer = document.querySelector("[data-cart-drawer]");
  const cartBackdrop = document.querySelector("[data-cart-backdrop]");
  const cartContent = document.querySelector("[data-cart-drawer-content]");
  const toast = document.querySelector(".toast");
  let toastTimer;

  const escapeHtml = (value) => String(value || "").replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "'": "&#39;",
    '"': "&quot;"
  })[character]);

  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || "en-US", {
    style: "currency",
    currency: theme.currency || window.Shopify?.currency?.active || "USD"
  }).format(Number(cents || 0) / 100);

  const refreshCartProgress = (cart) => {
    const progress = document.querySelector("[data-cart-progress]");
    const copy = document.querySelector("[data-cart-progress-copy]");
    if (!progress || !copy) return;
    const threshold = Number(progress.dataset.threshold || 0);
    const remaining = Math.max(0, threshold - Number(cart.total_price || 0));
    const percent = threshold > 0 ? Math.min(100, Number(cart.total_price || 0) / threshold * 100) : 100;
    progress.style.setProperty("--cart-progress", `${percent}%`);
    copy.textContent = remaining > 0 ? `${money(remaining)} away from free standard shipping.` : "Free standard shipping unlocked.";
  };

  function showToast(message) {
    if (!toast) return;
    window.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.classList.add("is-visible");
    toast.setAttribute("aria-hidden", "false");
    toastTimer = window.setTimeout(() => {
      toast.classList.remove("is-visible");
      toast.setAttribute("aria-hidden", "true");
    }, 3200);
  }

  function openCart() {
    if (!cartDrawer || !cartBackdrop) return;
    cartBackdrop.hidden = false;
    cartDrawer.classList.add("is-open");
    cartDrawer.setAttribute("aria-hidden", "false");
    body.classList.add("is-locked");
    cartDrawer.querySelector("[data-cart-close]")?.focus();
  }

  function closeCart() {
    if (!cartDrawer || !cartBackdrop) return;
    cartDrawer.classList.remove("is-open");
    cartDrawer.setAttribute("aria-hidden", "true");
    cartBackdrop.hidden = true;
    body.classList.remove("is-locked");
  }

  function renderCart(cart) {
    document.querySelectorAll("[data-cart-count]").forEach((element) => {
      element.textContent = String(cart.item_count || 0);
    });
    refreshCartProgress(cart);
    if (!cartContent) return;

    if (!cart.items?.length) {
      cartContent.innerHTML = `<div class="bag-empty"><p>Your bag is currently empty.</p><a class="button button-dark" href="${escapeHtml(theme.routes?.allProducts || "/collections/all")}">Explore the collection</a></div>`;
      return;
    }

    const items = cart.items.map((item, index) => {
      const image = item.image ? `<img src="${escapeHtml(item.image)}&width=180" alt="${escapeHtml(item.product_title)}" width="94" height="112">` : "";
      const variant = item.variant_title && item.variant_title !== "Default Title" ? item.variant_title : "";
      const plan = item.selling_plan_allocation?.selling_plan?.name || "";
      const detail = [variant, plan].filter(Boolean).join(" / ");
      return `<div class="bag-item" data-cart-line="${index + 1}">${image}<div><a href="${escapeHtml(item.url)}"><strong>${escapeHtml(item.product_title)}</strong></a>${detail ? `<p>${escapeHtml(detail)}</p>` : ""}<span>${money(item.final_line_price)}</span></div><button class="bag-remove" type="button" data-cart-remove="${index + 1}">Remove</button></div>`;
    }).join("");

    cartContent.innerHTML = `<div class="bag-content">${items}<div class="bag-total"><span>Subtotal</span><strong>${money(cart.total_price)}</strong></div><p class="bag-note">Shipping, discounts and taxes are calculated at checkout.</p><a class="button button-light checkout-button" href="${escapeHtml(theme.routes?.cart || "/cart")}">Review bag</a><a class="button button-dark" href="/checkout">Checkout</a></div>`;
  }

  async function refreshCart() {
    const response = await fetch(`${theme.routes?.cart || "/cart"}.js`, { headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error("Cart could not be loaded.");
    const cart = await response.json();
    renderCart(cart);
    return cart;
  }

  async function addToCart(form) {
    const submit = form.querySelector("[type='submit']");
    const message = form.querySelector("[data-product-message]");
    const formData = new FormData(form);
    formData.delete("purchase_option");
    submit?.setAttribute("aria-busy", "true");
    if (message) message.textContent = "";

    try {
      const response = await fetch(`${theme.routes?.cartAdd || "/cart/add"}.js`, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || "This product could not be added.");
      await refreshCart();
      openCart();
      showToast(`${result.product_title || "Product"} was added to your bag.`);
    } catch (error) {
      const text = error instanceof Error ? error.message : "This product could not be added.";
      if (message) message.textContent = text;
      showToast(text);
    } finally {
      submit?.removeAttribute("aria-busy");
    }
  }

  async function removeCartLine(line) {
    const response = await fetch(`${theme.routes?.cartChange || "/cart/change"}.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ line, quantity: 0 })
    });
    if (!response.ok) throw new Error("The item could not be removed.");
    renderCart(await response.json());
  }

  if (header) {
    const updateHeader = () => header.classList.toggle("is-scrolled", window.scrollY > 24);
    window.addEventListener("scroll", updateHeader, { passive: true });
    updateHeader();
  }

  menuToggle?.addEventListener("click", () => {
    const open = siteNav?.classList.toggle("is-open") || false;
    menuToggle.setAttribute("aria-expanded", String(open));
    menuToggle.setAttribute("aria-label", open ? "Close menu" : "Open menu");
  });

  document.addEventListener("click", async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const releaseAnchor = target.closest("[data-release-anchor]");
    if (releaseAnchor) {
      const destination = document.querySelector("#telemm-release-list");
      if (destination) {
        event.preventDefault();
        destination.scrollIntoView({ behavior: "smooth", block: "center" });
        destination.querySelector("input[type='email']")?.focus({ preventScroll: true });
      }
    }
    if (target.closest("[data-cart-open]")) {
      event.preventDefault();
      try { await refreshCart(); } catch { /* Server-rendered cart remains available. */ }
      openCart();
    }
    if (target.closest("[data-cart-close]") || target.matches("[data-cart-backdrop]")) closeCart();
    const removeButton = target.closest("[data-cart-remove]");
    if (removeButton) {
      removeButton.setAttribute("aria-busy", "true");
      try { await removeCartLine(Number(removeButton.dataset.cartRemove)); } catch (error) { showToast(error.message); }
    }
  });

  document.querySelectorAll("[data-product-form]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      addToCart(form);
    });
  });

  document.querySelectorAll("[data-selling-plan-choice]").forEach((choice) => {
    choice.addEventListener("change", () => {
      const form = choice.closest("form");
      const hidden = form?.querySelector("[data-selling-plan-input]");
      form?.querySelectorAll(".plan-card").forEach((card) => card.classList.toggle("is-selected", card.contains(choice)));
      if (hidden) {
        hidden.value = choice.value === "one-time" ? "" : choice.value;
        hidden.disabled = choice.value === "one-time";
      }
      const quantity = Number(form?.querySelector("[data-quantity-input]")?.value || 1);
      const price = Number(choice.dataset.price || 0) * quantity;
      const priceElement = form?.querySelector("[data-product-price]");
      if (priceElement) priceElement.textContent = money(price);
    });
  });

  document.querySelectorAll("[data-quantity-minus], [data-quantity-plus]").forEach((button) => {
    button.addEventListener("click", () => {
      const form = button.closest("form");
      const input = form?.querySelector("[data-quantity-input]");
      if (!input) return;
      const adjustment = button.hasAttribute("data-quantity-plus") ? 1 : -1;
      input.value = String(Math.max(1, Math.min(9, Number(input.value || 1) + adjustment)));
      form.querySelector("[data-selling-plan-choice]:checked")?.dispatchEvent(new Event("change", { bubbles: true }));
    });
  });

  document.querySelector("[data-variant-select]")?.addEventListener("change", (event) => {
    const select = event.currentTarget;
    const form = select.closest("form");
    form?.querySelector("[data-selling-plan-choice][value='one-time']")?.click();
    const price = Number(select.selectedOptions[0]?.dataset.price || 0);
    const priceElement = form?.querySelector("[data-product-price]");
    if (priceElement) priceElement.textContent = money(price);
  });

  document.querySelectorAll("[data-product-image]").forEach((thumbnail) => {
    thumbnail.addEventListener("click", () => {
      const image = document.querySelector("#ProductMainImage");
      if (!image) return;
      image.src = thumbnail.dataset.productImage;
      image.alt = thumbnail.dataset.productImageAlt || "TELEMM product view";
      document.querySelectorAll("[data-product-image]").forEach((item) => item.classList.toggle("is-active", item === thumbnail));
    });
  });

  document.querySelector("[data-scroll-to-product]")?.addEventListener("click", () => {
    const submit = document.querySelector("[data-product-submit]");
    if (!submit) return;
    submit.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => submit.focus({ preventScroll: true }), 450);
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    closeCart();
    siteNav?.classList.remove("is-open");
    menuToggle?.setAttribute("aria-expanded", "false");
  });
})();
